/* Responsive guardrails shared by the authenticated learning entry screens. */
.learningResponsiveSurface,
.learningResponsiveScreen {
  box-sizing: border-box;
  max-width: 100%;
  min-width: 0;
  overflow-x: clip;
}

.learningResponsiveSurface > *,
.learningResponsiveScreen > *,
.learningCatalogEntryHeader,
.learningCatalogEntryHeader > *,
.learningCatalogListRowBody,
.learningCatalogListRowText,
.learningCatalogListRowTrailing,
.learningMediaListHeader,
.learningMediaListContent,
.learningAudioPlayerCard,
.learningListeningQuizPrompt,
.learningListeningQuizMedia {
  box-sizing: border-box;
  min-width: 0;
  max-width: 100%;
}

.learningCatalogLegacySurface {
  box-sizing: border-box;
  background-color: var(--lms-color-surface-card);
  border: 1px solid var(--lms-color-border-subtle);
  border-radius: var(--lms-radius-xl);
  box-shadow: var(--lms-shadow-surface);
  color: var(--lms-color-ink-strong);
  padding: 1.5rem;
}

.learningCatalogLegacySurface[data-variant='flat'] {
  background-color: transparent;
  border: 0;
  border-radius: 0;
  box-shadow: none;
  padding: 0;
}

.learningCatalogLegacySurface[data-variant='flat'].learningResponsiveSurface {
  overflow: hidden;
}

.learningCatalogEntryHeader {
  position: sticky;
  top: 60px;
  right: 0;
  left: 0;
  z-index: 10;
  width: 100%;
  background-color: var(--lms-color-surface-card);
  border-bottom: 1px solid var(--lms-color-border-subtle);
  box-shadow: 0 10px 24px color-mix(in srgb, var(--lms-color-ink-strong) 10%, transparent);
}

.learningCatalogEntryHeaderInner {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  width: min(100%, 60rem);
  min-height: 0;
  margin-inline: auto;
  padding: 0.75rem;
}

.learningCatalogEntryActions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-end;
  gap: 0.75rem;
}

.learningCatalogLegacySurface[data-catalog-content='true'] {
  box-sizing: border-box;
  width: min(100%, 60rem);
  margin-inline: auto;
  overflow-x: hidden;
  padding: 1rem 0.75rem 0;
}

.learningCatalogRowTrailingStack {
  display: grid;
  gap: 0.75rem;
}

.learningCatalogActionLabel,
.learningCatalogQuizStatus,
.learningCatalogQuizAction {
  display: block;
  overflow-wrap: anywhere;
  font-size: 0.8125rem;
  line-height: 1.4;
  text-align: left;
}

.learningCatalogActionLabel,
.learningCatalogQuizAction {
  color: var(--lms-color-ink-strong);
  font-weight: 600;
}

.learningCatalogActionLabel {
  font-weight: 700;
}

.learningCatalogActionLabel.is-saving {
  color: var(--lms-color-ink-muted);
}

.learningCatalogActionLabel.is-current,
.learningCatalogQuizStatus.is-started {
  color: var(--lms-color-brand-action);
}

.learningCatalogQuizStatus {
  color: var(--lms-color-ink-muted);
  font-size: 0.875rem;
  font-weight: 700;
}

.learningCatalogLessonSection {
  margin-top: 2.5rem;
  padding-top: 1.5rem;
  border-top: 3px dashed var(--lms-color-brand-primary);
}

.learningCatalogLessonSection.is-first {
  margin-top: 0;
  padding-top: 0;
  border-top: 0;
}

.learningCatalogLessonStack,
.learningCatalogLessonHeading,
.learningCatalogLessonList {
  display: grid;
}

.learningCatalogLessonStack {
  gap: 1.5rem;
}

.learningCatalogLessonHeading {
  gap: 1rem;
}

.learningCatalogLessonList {
  gap: 1.25rem;
}

.learningCatalogLessonTitle {
  margin: 0;
  color: var(--lms-color-brand-action);
  font-size: 1.125rem;
  font-weight: 500;
  line-height: 1.4;
}

.learningCatalogLessonEmpty {
  margin: 0;
  color: var(--lms-color-ink-muted);
  font-size: 0.875rem;
  line-height: 1.7;
}

.learningCatalogWarning {
  box-sizing: border-box;
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  color: var(--lms-color-ink-strong);
  background-color: var(--lms-color-surface-warm);
  border: 1px solid var(--lms-color-border-subtle);
  border-radius: var(--lms-radius-md);
}

.learningCatalogLegacySurface[data-catalog-content='true'] .learnerAccessRequiredPanel {
  width: 100%;
  margin-bottom: 1.5rem;
}

.learningCatalogLockedBadge {
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 0.35rem;
  padding: 0.25rem 0.5rem;
  color: var(--lms-color-on-brand-primary);
  background-color: color-mix(in srgb, var(--lms-color-brand-primary) 86%, white);
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.learningCatalogActionLabel.is-locked,
.learningCatalogQuizAction.is-locked {
  color: var(--lms-color-ink-muted);
}

@media (min-width: 600px) {
  .learningCatalogEntryHeaderInner {
    min-height: 64px;
    padding: 0 1.25rem;
  }

  .learningCatalogEntryActions {
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
  }

  .learningCatalogLegacySurface[data-catalog-content='true'] {
    padding-top: 2rem;
    padding-right: 0;
    padding-left: 0;
  }

  .learningCatalogActionLabel,
  .learningCatalogQuizStatus,
  .learningCatalogQuizAction {
    text-align: right;
  }

  .learningCatalogLessonTitle {
    font-size: 1.5rem;
  }
}

.learningListeningQuizMedia img,
.learningListeningQuizMedia audio,
.learningListeningQuizMedia iframe {
  display: block;
  max-width: 100%;
}

.learningListeningQuizShell {
  min-height: 100vh;
}

.learningListeningQuizHeader {
  color: var(--lms-color-ink-strong);
  background-color: var(--lms-color-brand-primary);
  border-bottom: 1px solid var(--lms-color-border-subtle);
}

.learningListeningQuizHeaderSideEnd {
  justify-content: flex-end;
}

.learningListeningQuizHeaderToolbar {
  width: 100%;
  min-width: 0;
}

.learningListeningQuizHeaderIcon[data-slot='button'] {
  --lms-button-fg: var(--lms-color-ink-strong);
  --lms-button-hover-bg: var(--lms-color-surface-card);
  --lms-button-hover-fg: var(--lms-color-brand-action);

  width: 48px;
  height: 48px;
  padding: 12px;
}

.learningListeningQuizProgress {
  width: 100%;
  max-width: 43.75rem;
  min-width: 31.25rem;
  height: 0.75rem;
  margin: 1.25rem auto;
  overflow: hidden;
  background-color: var(--lms-color-surface-card);
  border-radius: 9999px;
}

.learningListeningQuizProgressBar {
  display: block;
  height: 100%;
  background-color: var(--lms-color-ink-strong);
  border-radius: inherit;
  transition: width 0.2s ease;
}

:root.dark .learningListeningQuizHeader {
  color: var(--lms-color-ink-strong);
  background-color: var(--lms-color-surface-card);
}

:root.dark .learningListeningQuizHeaderIcon[data-slot='button'] {
  --lms-button-hover-bg: var(--lms-color-surface-warm);
  --lms-button-hover-fg: var(--lms-color-brand-action);
}

:root.dark .learningListeningQuizProgress {
  background-color: var(--lms-color-surface-warm);
  border: 1px solid var(--lms-color-border-subtle);
}

:root.dark .learningListeningQuizProgressBar {
  background-color: var(--lms-color-brand-primary);
}

.learningListeningQuizContent {
  width: 100%;
}

.learningListeningQuizLesson {
  box-sizing: border-box;
  width: min(100%, 48rem);
  min-width: 0;
  margin-right: auto;
  margin-left: auto;
}

.learningListeningQuizLessonIntro {
  display: grid;
  gap: 0.5rem;
  padding-top: 0;
  padding-bottom: 2rem;
}

.learningListeningQuizLessonIntro > * {
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
}

.learningListeningQuizPrompt {
  width: 100%;
}

.learningListeningQuizPrompt,
.learningListeningQuizCompletion,
.learningListeningQuizPromptHeader,
.learningListeningQuizQuestions,
.learningListeningQuizAnswers,
.learningListeningQuizCompletionContent,
.learningListeningQuizCompletionHeader,
.learningListeningQuizCompletionCopy {
  box-sizing: border-box;
  min-width: 0;
  max-width: 100%;
}

.learningListeningQuizPrompt,
.learningListeningQuizCompletionContent {
  display: grid;
  gap: 1.5rem;
}

.learningListeningQuizPromptHeader,
.learningListeningQuizIntroCopy,
.learningListeningQuizCompletionCopy {
  display: grid;
  gap: 0.5rem;
}

.learningListeningQuizHeading,
.learningListeningQuizReviewItemTitle {
  margin: 0;
  color: var(--lms-color-ink-strong);
  font-size: clamp(1.35rem, 2vw, 1.75rem);
  font-weight: 700;
  line-height: 1.25;
}

.learningListeningQuizMutedText,
.learningListeningQuizReviewQuestion {
  margin: 0;
  color: var(--lms-color-ink-muted);
  line-height: 1.6;
}

.learningListeningQuizMedia {
  display: grid;
  gap: 1rem;
}

.learningListeningQuizAlert {
  display: flex;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.875rem 1rem;
  color: var(--lms-color-ink-strong);
  background-color: var(--lms-color-surface-card);
  border: 1px solid var(--lms-color-border-subtle);
  border-radius: var(--lms-radius-md);
}

.learningListeningQuizAlertMessage {
  min-width: 0;
  overflow-wrap: anywhere;
}

.learningListeningQuizAlert--error {
  border-color: var(--lms-color-danger);
}

.learningListeningQuizAlert--warning {
  border-color: var(--lms-color-warning);
}

.learningListeningQuizStatusBadge {
  display: inline-flex;
  min-height: 2rem;
  align-items: center;
  gap: 0.75rem;
  padding: 0 0.75rem;
  color: var(--lms-color-ink-strong);
  font-size: 0.8125rem;
  font-weight: 700;
  line-height: 1;
  background-color: var(--lms-color-surface-warm);
  border: 1px solid var(--lms-color-border-subtle);
  border-radius: var(--lms-radius-md);
}

.learningListeningQuizStatusBadge--success {
  background-color: color-mix(in srgb, var(--lms-color-success) 16%, var(--lms-color-surface-card));
  border-color: var(--lms-color-success);
}

.learningListeningQuizStatusBadge--warning {
  background-color: color-mix(in srgb, var(--lms-color-warning) 26%, var(--lms-color-surface-card));
  border-color: var(--lms-color-warning);
}

.learningListeningQuizPrompt > *,
.learningListeningQuizCompletionContent > * {
  min-width: 0;
  max-width: 100%;
}

.learningListeningQuizPromptHeader > *,
.learningListeningQuizCompletionCopy > * {
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
}

.learningListeningQuizQuestions,
.learningListeningQuizAnswers,
.learningListeningQuizCompletionContent {
  width: 100%;
}

.learningListeningQuizQuestions,
.learningListeningQuizAnswers {
  overflow: visible;
}

.learningListeningQuizCompletionHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  width: 100%;
}

.learningListeningQuizCompletionCopy {
  flex: 1 1 16rem;
}

.learningListeningQuizCompletionMetrics {
  display: flex;
  gap: 1rem;
  min-width: 0;
}

.learningListeningQuizMetric {
  box-sizing: border-box;
  flex: 1 1 0;
  width: 100%;
  max-width: 100%;
  padding: 1.25rem;
  background-color: var(--lms-color-surface-card);
  border: 1px solid var(--lms-color-border-subtle);
  border-radius: var(--lms-radius-md);
}

.learningListeningQuizMetricBody {
  display: grid;
  gap: 0.5rem;
}

.learningListeningQuizMetricLabel {
  color: var(--lms-color-ink-muted);
  font-size: 0.875rem;
  font-weight: 600;
}

.learningListeningQuizMetricValue {
  color: var(--lms-color-ink-strong);
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.1;
}

.learningListeningQuizMetricValueWithIcon {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.learningListeningQuizMetricIcon--success { color: var(--lms-color-success); }
.learningListeningQuizMetricIcon--warning { color: var(--lms-color-warning); }

.learningListeningQuizReviewDialog {
  color: var(--lms-color-ink-strong);
  background-color: var(--lms-color-surface-card);
  border-color: var(--lms-color-border-subtle);
}

.learningListeningQuizReviewList {
  display: grid;
  gap: 0.75rem;
}

.learningListeningQuizReviewItem {
  display: grid;
  gap: 0.75rem;
  padding: 1rem;
  background-color: var(--lms-color-surface-card);
  border: 1px solid var(--lms-color-border-subtle);
  border-radius: var(--lms-radius-md);
}

.learningListeningQuizReviewItemHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.learningListeningQuizReviewItemTitle {
  font-size: 1rem;
}

.learningListeningQuizImageSurface,
.learningListeningQuizVideoSurface {
  width: 100%;
  min-width: 0;
  overflow: hidden;
  background-color: var(--lms-color-surface-card);
  border: 1px solid var(--lms-color-border-subtle);
  border-radius: var(--lms-radius-xl);
}

.learningListeningQuizImageSurface {
  display: flex;
  min-height: 12rem;
  max-height: 22.5rem;
  align-items: center;
  justify-content: center;
}

.learningListeningQuizImageSurface img {
  display: block;
  width: 100%;
  max-width: 100%;
  height: auto;
  max-height: 22.5rem;
  object-fit: contain;
}

.learningListeningQuizVideoSurface {
  aspect-ratio: 16 / 9;
}

.learningListeningQuizVideoSurface iframe {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
}

.learningListeningQuizAudioBar {
  width: 100%;
}

.learningListeningQuizFallback {
  width: 100%;
  margin-top: 1rem;
}

.learningListeningQuizCompletion {
  width: 100%;
}

.learningListeningQuizCompletionMetrics {
  width: 100%;
}

.learningListeningQuizMetric {
  min-width: 0;
}

.learningListeningQuizFooter {
  display: flex;
  justify-content: center;
  padding: 1rem 1.25rem calc(1rem + env(safe-area-inset-bottom));
}

.learningListeningQuizFooterInner {
  width: min(100%, 20rem);
}

.learningListeningQuizFooterInner .learningListeningQuizContinue {
  width: 100%;
}

@media (max-width: 580px) {
  .learningCatalogListRowBody {
    align-items: stretch;
    gap: 0.75rem;
  }

  .learningCatalogListRowAvatar {
    margin-inline: auto;
  }

  .learningCatalogListRowText {
    width: 100%;
    padding-inline: 0;
    text-align: center;
    overflow-wrap: anywhere;
  }

  .learningCatalogListRowTrailing {
    width: 100%;
  }

  .learningCatalogListRowTrailing .learningCatalogRowTrailingStack {
    max-width: 100%;
    overflow-wrap: anywhere;
  }

  .learningCatalogListRowTrailing .learningCatalogActionLabel,
  .learningCatalogListRowTrailing .learningCatalogQuizStatus,
  .learningCatalogListRowTrailing .learningCatalogQuizAction {
    text-align: center;
  }

  .learningMediaListHeader,
  .learningMediaListContent,
  .learningAudioPlayerCard {
    width: 100%;
  }

  .learningAudioPlayerCard audio {
    width: 100%;
  }

  .learningListeningQuizContinue {
    width: 100%;
  }

  .learningListeningQuizProgress {
    max-width: none;
    min-width: 0;
  }

  .learningListeningQuizHeaderIcon[data-slot='button'] {
    width: 44px;
    height: 44px;
    padding: 10px;
  }

  .learningListeningQuizProgress {
    height: 0.625rem;
  }

  .learningListeningQuizLessonIntro {
    padding-top: 0;
    padding-bottom: 1.5rem;
  }

  .learningListeningQuizImageSurface {
    min-height: 10rem;
    max-height: 20rem;
  }

  .learningListeningQuizImageSurface img {
    max-height: 20rem;
  }

  .learningListeningQuizFooter {
    padding-right: 1rem;
    padding-left: 1rem;
  }

  .learningListeningQuizCompletionHeader,
  .learningListeningQuizCompletionMetrics {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
  }

  .learningListeningQuizAlert {
    align-items: stretch;
    flex-direction: column;
  }

  .learningListeningQuizReviewItemHeader {
    align-items: flex-start;
    flex-direction: column;
  }
}
